import { useState, useEffect, useRef } from 'react';
import { Sun, Moon, Monitor, KeyRound, ShieldCheck, ChevronDown } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import PinChangeModal from '../components/PinChangeModal';
import PrepinacServeru from '../components/aplikace/PrepinacServeru';

const THEMES = [
    { v: 'light',  Icon: Sun,     label: 'Světlý' },
    { v: 'dark',   Icon: Moon,    label: 'Tmavý'  },
    { v: 'system', Icon: Monitor, label: 'Systém' },
];

const EMAIL_DOMAIN = '@fofrlama.cz';

function DevLoginDropdown({ devUsers, isDark, roleColors, roleLabels, onSelect }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div ref={ref} style={{
            marginTop: '20px',
            paddingTop: '20px',
            borderTop: '1px solid var(--border-subtle)',
        }}>
            <button
                onClick={() => setOpen(!open)}
                style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1px solid var(--border-subtle)',
                    background: open ? (isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-elevated)') : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: 'var(--text-muted)',
                    fontSize: '13px',
                    fontWeight: 500,
                    transition: 'all 0.15s ease',
                }}
                onMouseEnter={e => {
                    e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-elevated)';
                    e.currentTarget.style.borderColor = 'var(--border-default)';
                }}
                onMouseLeave={e => {
                    if (!open) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.borderColor = 'var(--border-subtle)';
                    }
                }}
            >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ShieldCheck size={14} />
                    Dev přihlášení
                </span>
                <ChevronDown size={14} style={{
                    transition: 'transform 0.2s ease',
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                }} />
            </button>

            {open && (
                <div style={{
                    marginTop: '6px',
                    borderRadius: '10px',
                    border: '1px solid var(--border-default)',
                    background: isDark ? 'rgba(20,20,30,0.95)' : 'var(--surface-raised)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    overflow: 'hidden',
                }}>
                    {devUsers.map((u, i) => (
                        <button
                            key={u.id}
                            onClick={() => { setOpen(false); onSelect(u.id); }}
                            style={{
                                width: '100%',
                                textAlign: 'left',
                                padding: '10px 14px',
                                border: 'none',
                                borderBottom: i < devUsers.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                transition: 'background 0.12s ease',
                                color: 'var(--text-primary)',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                            <div style={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '8px',
                                background: 'var(--brand-bg)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'var(--brand)',
                                fontSize: '11px',
                                fontWeight: 700,
                                flexShrink: 0,
                            }}>
                                {u.jmeno?.[0]}{u.prijmeni?.[0]}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', margin: 0 }}>
                                    {u.jmeno} {u.prijmeni}
                                </p>
                                <span style={{ fontSize: '11px', fontWeight: 600, color: roleColors[u.role] }}>
                                    {roleLabels[u.role]}
                                </span>
                                {u.zamestnanec_id && (
                                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: '4px' }}>· zaměstnanec</span>
                                )}
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function LoginScreen({ onLogin }) {
    const [devUsers, setDevUsers] = useState([]);
    const [isDev, setIsDev] = useState(false);
    const [loginId, setLoginId] = useState('');
    const [pinCode, setPinCode] = useState('');
    const [pinError, setPinError] = useState('');
    const [pinLoading, setPinLoading] = useState(false);
    const [showPinChange, setShowPinChange] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const { theme, setTheme } = useTheme();
    const isDark = theme === 'dark' ||
        (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    useEffect(() => {
        fetch('/auth/dev-users')
            .then(r => r.json())
            .then(data => {
                if (data.length > 0) {
                    setDevUsers(data);
                    setIsDev(true);
                }
            })
            .catch(() => {});
    }, []);

    const handleDevLogin = async (id) => {
        await fetch('/sanctum/csrf-cookie');
        const token = document.cookie
            .split('; ')
            .find(row => row.startsWith('XSRF-TOKEN='))
            ?.split('=')[1];

        const res = await fetch('/auth/dev-login', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-XSRF-TOKEN': decodeURIComponent(token || ''),
            },
            body: JSON.stringify({ uzivatel_id: id }),
        });
        if (res.ok) {
            const me = await fetch('/auth/me').then(r => r.json());
            onLogin(me);
        }
    };

    const handlePinLogin = async (e) => {
        e.preventDefault();
        setPinError('');
        setPinLoading(true);

        const email = loginId.includes('@') ? loginId : `${loginId}${EMAIL_DOMAIN}`;

        try {
            await fetch('/sanctum/csrf-cookie');
            const token = document.cookie
                .split('; ')
                .find(row => row.startsWith('XSRF-TOKEN='))
                ?.split('=')[1];

            const res = await fetch('/auth/pin-login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-XSRF-TOKEN': decodeURIComponent(token || ''),
                },
                body: JSON.stringify({ email, pin: pinCode, remember: rememberMe }),
            });

            if (!res.ok) {
                const data = await res.json();
                setPinError(data.error || 'Neplatné přihlašovací údaje');
                setPinLoading(false);
                return;
            }

            const loginData = await res.json();

            if (!loginData.pin_zmeneno) {
                setShowPinChange(true);
                setPinLoading(false);
                return;
            }

            const me = await fetch('/auth/me').then(r => r.json());
            onLogin(me);
        } catch {
            setPinError('Chyba při přihlášení');
        }
        setPinLoading(false);
    };

    const handlePinChanged = async () => {
        setShowPinChange(false);
        const me = await fetch('/auth/me').then(r => r.json());
        onLogin(me);
    };

    const roleColors = {
        superadmin: 'var(--brand)',
        admin: '#3b82f6',
        uzivatel: 'var(--text-muted)',
    };
    const roleLabels = { superadmin: 'Super Admin', admin: 'Admin', uzivatel: 'Uživatel' };

    const logoSrc = isDark ? '/images/logo-white.png' : '/images/logo-dark.png';

    return (
        <div style={{
            minHeight: '100vh',
            background: 'var(--surface-base)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            position: 'relative',
            overflow: 'hidden',
        }}>
            {/* Background glow */}
            <div style={{
                position: 'absolute',
                top: '-20%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '600px',
                height: '600px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, var(--brand-bg) 0%, transparent 70%)',
                pointerEvents: 'none',
            }} />
            <div style={{
                position: 'absolute',
                bottom: '-30%',
                right: '-10%',
                width: '500px',
                height: '500px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, var(--brand-purple-bg) 0%, transparent 70%)',
                pointerEvents: 'none',
            }} />

            <div style={{ width: '100%', maxWidth: '420px', position: 'relative', zIndex: 1 }}>
                {/* Logo + brand */}
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '80px',
                        height: '80px',
                        borderRadius: '20px',
                        background: 'var(--surface-raised)',
                        border: '1px solid var(--border-default)',
                        boxShadow: 'var(--shadow-md)',
                        marginBottom: '16px',
                    }}>
                        <img
                            src={logoSrc}
                            alt="FofrLama"
                            style={{ width: '52px', height: '52px', objectFit: 'contain' }}
                        />
                    </div>
                    <h1 style={{
                        fontSize: '28px',
                        fontWeight: 800,
                        color: 'var(--brand)',
                        letterSpacing: '-0.5px',
                        margin: '0 0 4px 0',
                    }}>
                        FofrLama
                    </h1>
                    <p style={{
                        fontSize: '14px',
                        color: 'var(--text-muted)',
                        margin: 0,
                        fontWeight: 500,
                        letterSpacing: '2px',
                        textTransform: 'uppercase',
                    }}>
                        Operating System
                    </p>
                </div>

                {/* Main card */}
                <div style={{
                    background: 'var(--surface-raised)',
                    border: '1px solid var(--border-default)',
                    borderRadius: '16px',
                    padding: '28px',
                    boxShadow: 'var(--shadow-lg)',
                    backdropFilter: 'blur(20px)',
                }}>
                    <form onSubmit={handlePinLogin} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ position: 'relative' }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'stretch',
                                borderRadius: '10px',
                                border: '1px solid var(--border-default)',
                                background: 'var(--surface-elevated)',
                                overflow: 'hidden',
                            }}>
                                <input
                                    type="text"
                                    placeholder="přihlašovací jméno"
                                    value={loginId}
                                    onChange={e => {
                                        const v = e.target.value.toLowerCase().replace(/\s+/g, '').replace(/@.*$/, '');
                                        setLoginId(v);
                                    }}
                                    name="fofrlama-login-id"
                                    autoComplete="off"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    required
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        padding: '12px 14px',
                                        border: 'none',
                                        background: 'transparent',
                                        color: 'var(--text-primary)',
                                        fontSize: '14px',
                                        outline: 'none',
                                    }}
                                />
                                <span style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '0 14px',
                                    fontSize: '14px',
                                    color: 'var(--text-muted)',
                                    background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                                    borderLeft: '1px solid var(--border-subtle)',
                                    userSelect: 'none',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {EMAIL_DOMAIN}
                                </span>
                            </div>
                        </div>

                        <input
                            type="password"
                            inputMode="numeric"
                            pattern="\d{4,8}"
                            placeholder="PIN (4-8 číslic)"
                            value={pinCode}
                            onChange={e => {
                                const v = e.target.value.replace(/\D/g, '').slice(0, 8);
                                setPinCode(v);
                            }}
                            autoComplete="current-password"
                            required
                            style={{
                                width: '100%',
                                padding: '12px 14px',
                                borderRadius: '10px',
                                border: '1px solid var(--border-default)',
                                background: 'var(--surface-elevated)',
                                color: 'var(--text-primary)',
                                fontSize: '14px',
                                outline: 'none',
                                letterSpacing: '4px',
                                boxSizing: 'border-box',
                            }}
                        />
                        {pinError && (
                            <p style={{ fontSize: '13px', color: '#EF4444', margin: 0, textAlign: 'center' }}>
                                {pinError}
                            </p>
                        )}
                        <button
                            type="submit"
                            disabled={pinLoading || pinCode.length < 4 || !loginId}
                            style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                padding: '12px 16px',
                                borderRadius: '10px',
                                border: 'none',
                                background: 'var(--brand)',
                                color: '#fff',
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: pinLoading || pinCode.length < 4 || !loginId ? 'not-allowed' : 'pointer',
                                opacity: pinLoading || pinCode.length < 4 || !loginId ? 0.5 : 1,
                                transition: 'opacity 0.15s ease',
                            }}
                        >
                            <KeyRound size={16} />
                            {pinLoading ? 'Přihlašuji...' : 'Přihlásit PINem'}
                        </button>
                    </form>

                    <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginTop: '12px',
                        cursor: 'pointer',
                        userSelect: 'none',
                    }}>
                        <input
                            type="checkbox"
                            checked={rememberMe}
                            onChange={e => setRememberMe(e.target.checked)}
                            style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: 'var(--brand)' }}
                        />
                        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                            Trvale přihlášen na tomto zařízení
                        </span>
                    </label>

                    {/* Dev login — dropdown */}
                    {isDev && devUsers.length > 0 && (
                        <DevLoginDropdown
                            devUsers={devUsers}
                            isDark={isDark}
                            roleColors={roleColors}
                            roleLabels={roleLabels}
                            onSelect={handleDevLogin}
                        />
                    )}
                </div>

                {/* Mobilní aplikace: Ostrý / Test (na webu nic) */}
                <PrepinacServeru />

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: '24px',
                    padding: '0 4px',
                }}>
                    <p style={{
                        fontSize: '12px',
                        color: 'var(--text-muted)',
                        margin: 0,
                    }}>
                        v{document.querySelector('meta[name="app-version"]')?.content || '?'} · Bojujeme proti Hladu a Nudě
                    </p>

                    {/* Theme switcher */}
                    <div style={{
                        display: 'flex',
                        gap: '2px',
                        background: 'var(--surface-elevated)',
                        borderRadius: '8px',
                        padding: '3px',
                        border: '1px solid var(--border-subtle)',
                    }}>
                        {THEMES.map(({ v, Icon, label }) => (
                            <button
                                key={v}
                                onClick={() => setTheme(v)}
                                title={label}
                                style={{
                                    width: '28px',
                                    height: '28px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    background: theme === v ? 'var(--brand-bg)' : 'transparent',
                                    color: theme === v ? 'var(--brand)' : 'var(--text-muted)',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.15s ease',
                                }}
                            >
                                <Icon size={14} strokeWidth={2} />
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {showPinChange && (
                <PinChangeModal
                    firstLogin={true}
                    onComplete={handlePinChanged}
                    onClose={() => setShowPinChange(false)}
                />
            )}
        </div>
    );
}
